import { F_DEFAULTS } from '@/features/filters/filter-fns';
import type { Filters, PresetId, Zone } from '@/features/filters/types';
import type { Answers } from './types';

export interface ItalyPreset {
  filters: Filters;
  layer?: string;
  preset?: PresetId;
}

// Maps quiz answers → Italy map filters. Italy-specific by nature:
// climate types, terrain zones, choropleth layer names are local to Italy data model.
// Other countries will get their own mapper when their datasets are integrated.
export function answersToItalyFilters(answers: Answers): ItalyPreset {
  const filters: Filters = { ...F_DEFAULTS, zones: new Set<Zone>() };
  let layer: string | undefined;
  let preset: PresetId | undefined;

  const climate = answers.climate?.optionIds ?? [];
  const dealbreakers = answers.dealbreakers?.optionIds ?? [];
  const priorities = answers.priorities?.optionIds ?? [];

  const zones = new Set<Zone>();
  let altMin = filters.alt_min;
  let altMax = filters.alt_max;

  for (const c of climate) {
    if (c === 'alps') {
      zones.add('montagna_interna');
      zones.add('montagna_litoranea');
      altMin = Math.max(altMin, 600);
    } else if (c === 'mediterranean') {
      zones.add('collina_litoranea');
      zones.add('pianura');
    } else if (c === 'continental') {
      zones.add('pianura');
      zones.add('collina_interna');
    } else if (c === 'northern') {
      zones.add('montagna_interna');
      altMin = Math.max(altMin, 400);
    }
    // 'tropical' and 'any' don't constrain Italy filters.
  }

  // Single-climate hint → pick a preset that mirrors it (UI presets are curated).
  if (climate.length === 1) {
    if (climate[0] === 'alps') preset = 'alpine';
    else if (climate[0] === 'mediterranean') preset = 'lakeside';
  }

  // Deal-breakers
  if (dealbreakers.includes('hot-summer')) {
    filters.t_jul_max = 30;
  }
  if (dealbreakers.includes('humidity')) {
    filters.hum_max = 70;
  }
  if (dealbreakers.includes('snow')) {
    altMax = Math.min(altMax, 800);
  }

  // Priority → which choropleth layer to open by default
  if (priorities.includes('healthcare')) {
    layer = 'hospital';
  } else if (priorities.includes('safety')) {
    layer = 't_jan';
  } else if (priorities.includes('schools')) {
    layer = 'train';
  } else if (priorities.includes('transport')) {
    layer = 'train';
  } else if (priorities.includes('nature')) {
    layer = 'altitude';
  } else if (priorities.includes('ru-community')) {
    layer = 'foreign_ru';
  }

  if (zones.size > 0) filters.zones = zones;
  filters.alt_min = altMin;
  filters.alt_max = altMax;

  return { filters, layer, preset };
}

// Generates a short human-readable summary of which Italy map filters will be
// pre-loaded from quiz answers. Shown below the "Open Italy map →" CTA so the
// user understands the map is personalised, not a blank slate.
// Returns null when no meaningful filters were derived (e.g. "any" climate, no deal-breakers).
export function buildMapBridgeTeaser(answers: Answers, lang: 'en' | 'ru'): string | null {
  const en = lang === 'en';
  const parts: string[] = [];

  const climate = answers.climate?.optionIds ?? [];
  const dealbreakers = answers.dealbreakers?.optionIds ?? [];

  if (climate.includes('alps')) {
    parts.push(en ? 'alpine zones (600m+)' : 'горные зоны (600м+)');
  } else if (climate.includes('northern')) {
    parts.push(en ? 'highlands (400m+)' : 'нагорья (400м+)');
  } else if (climate.includes('mediterranean')) {
    parts.push(en ? 'coastal climate' : 'средиземноморский климат');
  } else if (climate.includes('continental')) {
    parts.push(en ? 'plains & hills' : 'равнины и холмы');
  }

  if (dealbreakers.includes('hot-summer')) {
    parts.push(en ? 'max July 30°C' : 'макс. июль 30°C');
  }
  if (dealbreakers.includes('humidity')) {
    parts.push(en ? 'max humidity 70%' : 'макс. влажность 70%');
  }
  if (dealbreakers.includes('snow')) {
    parts.push(en ? 'below 800m' : 'до 800м высоты');
  }

  if (parts.length === 0) return null;

  const shown = parts.slice(0, 3);
  return (en ? 'Pre-loaded: ' : 'Предзагрузка: ') + shown.join(' · ');
}
